import { XHRRequestCanceler } from '../../../../Libs/xhr/XHRRequestCanceler';
import {
    IVisiteMedicaleDataService,
    VisiteMedicaleDataServiceName,
    VisiteMedicaleEndpoints,
} from '../../../DataServices/VisiteMedicaleDataService';
import { Lazy } from '../../../Helpers/Lazy';
import { BaseApiDataResponse } from '../../../Models/ApiData';
import {
    VisiteMedicale,
    VisiteMedicaleDetailsApiDataResponse,
    VisiteMedicaleListOrder,
    VisiteMedicaleListResponse,
} from '../../../Models/VisiteMedicale/VisiteMedicale';
import { ExportFileType, ExportUtils } from '../../../Utils/ExportUtils';
import { IoC } from '../../ServicesContainer';
import { BaseReduxService } from '../Api/BaseReduxService';
import { AuthServiceName, IAuthService } from '../Authentication/AuthenticationService';
import { DownloadServiceName, IDownloadService } from '../Download/DownloadService';
import { I18nServiceName, II18nService } from '../I18n/I18nService';

export interface IVisiteMedicaleService {
    listVisiteMedicale: (
        cts: XHRRequestCanceler,
        params: {
            search?: string;
            order?: VisiteMedicaleListOrder;
        }
    ) => Promise<VisiteMedicaleListResponse>;

    getVisiteMedicale: (
        cts: XHRRequestCanceler,
        visiteMedicaleId: string
    ) => Promise<VisiteMedicaleDetailsApiDataResponse>;

    updateVisiteMedicale: (
        visiteMedicaleId: string,
        visiteMedicale: VisiteMedicale,
        cts: XHRRequestCanceler
    ) => Promise<VisiteMedicaleDetailsApiDataResponse>;

    createVisiteMedicale: (
        visiteMedicale: VisiteMedicale,
        cts: XHRRequestCanceler
    ) => Promise<VisiteMedicaleDetailsApiDataResponse>;

    exportVisiteMedicaleList: (
        cts: XHRRequestCanceler,
        search: string,
        order?: VisiteMedicaleListOrder
    ) => Promise<void>;

    deleteVisiteMedicale: (
        visiteMedicaleId: string,
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;
}

class VisiteMedicaleService extends BaseReduxService implements IVisiteMedicaleService {
    private readonly visiteMedicaleDataService: Lazy<IVisiteMedicaleDataService>;
    private readonly i18nService: Lazy<II18nService>;
    private readonly downloadService: Lazy<IDownloadService>;
    private readonly authService: Lazy<IAuthService>;

    constructor() {
        super();
        this.visiteMedicaleDataService = IoC.getLazy<IVisiteMedicaleDataService>(
            VisiteMedicaleDataServiceName
        );
        this.i18nService = IoC.getLazy<II18nService>(I18nServiceName);
        this.downloadService = IoC.getLazy<IDownloadService>(DownloadServiceName);
        this.authService = IoC.getLazy<IAuthService>(AuthServiceName);
    }

    deleteVisiteMedicale = (
        visiteMedicaleId: string,
        cts: XHRRequestCanceler
    ): Promise<BaseApiDataResponse> => {
        try {
            return this.visiteMedicaleDataService
                .value()
                .deleteVisiteMedicale(visiteMedicaleId, cts);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    listVisiteMedicale = (
        cts: XHRRequestCanceler,
        params: { search?: string | undefined; order?: VisiteMedicaleListOrder | undefined }
    ): Promise<VisiteMedicaleListResponse> => {
        try {
            return this.visiteMedicaleDataService.value().listVisiteMedicale(
                {
                    search: params.search,
                    order: params.order,
                },
                cts
            );
        } catch (err) {
            return Promise.reject(err);
        }
    };

    getVisiteMedicale = (
        cts: XHRRequestCanceler,
        visiteMedicaleId: string
    ): Promise<VisiteMedicaleDetailsApiDataResponse> => {
        try {
            return this.visiteMedicaleDataService.value().getVisiteMedicale(cts, visiteMedicaleId);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    updateVisiteMedicale = async (
        visiteMedicaleId: string,
        visiteMedicale: VisiteMedicale,
        cts: XHRRequestCanceler
    ): Promise<VisiteMedicaleDetailsApiDataResponse> => {
        try {
            const response = await this.visiteMedicaleDataService
                .value()
                .updateVisiteMedicale(visiteMedicaleId, visiteMedicale, cts);
            if (!response.is_success) {
                this.toastApiError(
                    this.i18nService.value().getString(`Error_${response.error_code}` || '')
                );
            }
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    createVisiteMedicale = async (
        visiteMedicale: VisiteMedicale,
        cts: XHRRequestCanceler
    ): Promise<VisiteMedicaleDetailsApiDataResponse> => {
        try {
            const response = await this.visiteMedicaleDataService
                .value()
                .createVisiteMedicale(visiteMedicale, cts);
            if (!response.is_success) {
                this.toastApiError(
                    this.i18nService.value().getString(`Error_${response.error_code}` || '')
                );
            }
            return Promise.resolve(response);
        } catch (err) {
            this.toastApiError(this.i18nService.value().getString(`Error_${err.error_code}` || ''));
            return Promise.reject(err);
        }
    };

    exportVisiteMedicaleList = async (
        cts: XHRRequestCanceler,
        search: string,
        order?: VisiteMedicaleListOrder | undefined
    ): Promise<void> => {
        try {
            const params: { [key: string]: string } = {};
            params['search'] = search;
            if (order) {
                params['order'] = order.valueOf();
            }
            const url: string = ExportUtils.getExportUrlWithParams(
                {
                    url: VisiteMedicaleEndpoints.EXPORT_VISITE_MEDICALE_LIST,
                    auth: await this.authService.value().getAuthorization(),
                },
                params
            );
            this.downloadService
                .value()
                .downloadFile(url, 'Visites_Medicales', ExportFileType.EXCEL, cts);
        } catch (err) {
            Promise.reject(err);
        }
    };
}

const VisiteMedicaleServiceName = 'VisiteMedicaleService';
export { VisiteMedicaleService, VisiteMedicaleServiceName };
